import { finishRecording, registerRecording } from "./api.js";
import { publishedAttributes } from "./in-app-identity.js";
import { loadErizo } from "./erizo.js";
import { MediaDiagnostics } from "./media-diagnostics.js";
import { blurControl, blurStatus, setBackgroundBlur } from "./background-blur.js";
import { switchCameraTrack } from "./camera-switch.js";

const SILENT_WAV =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";

export function unlockAudio() {
  let el = document.getElementById("bm-audio-unlock");
  if (!el) {
    el = document.createElement("audio");
    el.id = "bm-audio-unlock";
    el.setAttribute("playsinline", "true");
    el.preload = "auto";
    el.src = SILENT_WAV;
    el.volume = 0.01;
    document.body.appendChild(el);
  }
  el.play().catch(() => undefined);
  document.querySelectorAll("audio").forEach((node) => {
    if (node.id === "bm-audio-unlock" || node.dataset.localMic === "1") return;
    node.muted = false;
    node.volume = 1;
    node.play?.().catch(() => undefined);
  });
}

function mediaStreamOf(stream) {
  if (!stream) return null;
  const raw = stream.stream || stream.mediaStream;
  if (raw && typeof raw.getAudioTracks === "function") return raw;
  try {
    if (typeof stream.getNativeMediaStream === "function") {
      const ms = stream.getNativeMediaStream();
      if (ms && typeof ms.getAudioTracks === "function") return ms;
    }
    if (typeof stream.getMediaStream === "function") {
      const ms = stream.getMediaStream();
      if (ms && typeof ms.getAudioTracks === "function") return ms;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function isLocalId(stream, local, screen, camera) {
  const id = stream?.getID?.();
  if (!id) return false;
  return id === local?.getID?.() || id === screen?.getID?.() || id === camera?.getID?.();
}

function isVideoOnly(stream) {
  const a = stream?.getAttributes?.() || {};
  return a.type === "video" || a.type === "screen" || a.screen === true;
}

function liveAudioTracks(ms) {
  return (ms?.getAudioTracks?.() || []).filter((t) => t.readyState === "live");
}

function liveVideoTracks(ms) {
  return (ms?.getVideoTracks?.() || []).filter((t) => t.readyState === "live");
}

function stopTracks(ms) {
  ms?.getTracks?.().forEach((t) => {
    try {
      t.onended = null;
      t.stop();
    } catch {
      /* ignore */
    }
  });
}

function keepLive(ms) {
  const tracks = [...liveVideoTracks(ms), ...liveAudioTracks(ms)];
  if (!tracks.length) return null;
  try {
    return new MediaStream(tracks.map((t) => t.clone()));
  } catch {
    return ms;
  }
}

function trackDeviceId(track) {
  try {
    return String(track?.getSettings?.().deviceId || "");
  } catch {
    return "";
  }
}

function eventMessage(evt, fallback) {
  const m = evt?.message || evt?.msg || evt?.error;
  if (typeof m === "string" && m.trim() && m !== "undefined") return m;
  return fallback;
}

function listOfKind(devices, kind, fallback) {
  const seen = new Set();
  const out = [];
  for (const d of devices) {
    if (d.kind !== kind || !d.deviceId || seen.has(d.deviceId)) continue;
    seen.add(d.deviceId);
    out.push({
      deviceId: d.deviceId,
      label: String(d.label || "").trim() || fallback,
      groupId: d.groupId || "",
    });
  }
  return out;
}

function deviceDiff(prev, next) {
  const prevList = prev || [];
  const nextList = next || [];
  return {
    added: nextList.filter((n) => !prevList.some((p) => p.deviceId === n.deviceId)),
    removed: prevList.filter((p) => !nextList.some((n) => n.deviceId === p.deviceId)),
  };
}

function rtcPeerConnections(stream) {
  const wrap = stream?.pc;
  if (!wrap) return [];
  const out = [];
  const take = (conn) => {
    const pc = conn?.peerConnection || conn?.stack?.peerConnection;
    if (pc && typeof pc.getSenders === "function") out.push(pc);
  };
  if (typeof wrap.forEach === "function") wrap.forEach((c) => take(c));
  else take(wrap);
  return out;
}

function isAgentnetAddr(addr) {
  const s = String(addr || "");
  return s === "10.86" || s.startsWith("10.86.");
}

function peerUserId(stream, extra) {
  const a = stream?.getAttributes?.() || {};
  return String(extra?.userid || extra?.userId || a.userid || a.userId || a.id || "").trim();
}

function emptyPcStats() {
  return { packetsIn: 0, packetsOut: 0, agentnet: false, iceFailed: false };
}

/** Path watchdog: poll / quiet after join / consecutive bad ICE before rejoin / cooldown after a rejoin. */
const PATH_POLL_MS = 3000;
const PATH_QUIET_MS = 8000;
const PATH_QUIET_TURN_MS = 15000;
const PATH_CONFIRM_MS = 15000;
const PATH_COOLDOWN_MS = 20000;
const STREAM_REPAIR_TIMEOUT_MS = 10000;
const STREAM_REPAIR_MAX = 2;

export async function inspectPc(pc) {
  const explicitlyFailed =
    pc?.iceConnectionState === "failed" || pc?.connectionState === "failed";
  if (!pc?.getStats) return { ...emptyPcStats(), iceFailed: explicitlyFailed };
  let stats;
  try {
    stats = await pc.getStats();
  } catch {
    return { ...emptyPcStats(), iceFailed: explicitlyFailed };
  }
  const byId = new Map();
  stats.forEach((r) => byId.set(r.id, r));
  let packetsIn = 0;
  let packetsOut = 0;
  let agentnet = false;
  stats.forEach((r) => {
    const audio = r.kind === "audio" || r.mediaType === "audio";
    if (r.type === "inbound-rtp" && audio) packetsIn += r.packetsReceived || 0;
    if (r.type === "outbound-rtp" && audio) packetsOut += r.packetsSent || 0;
    if (r.type !== "candidate-pair") return;
    if (r.nominated !== true && r.selected !== true) return;
    const loc = byId.get(r.localCandidateId);
    const rem = byId.get(r.remoteCandidateId);
    const lip = loc?.address || loc?.ip || "";
    const rip = rem?.address || rem?.ip || "";
    if (isAgentnetAddr(lip) || isAgentnetAddr(rip)) agentnet = true;
  });
  return { packetsIn, packetsOut, agentnet, iceFailed: explicitlyFailed };
}

export class RoomClient {
  constructor(token, username) {
    this.token = token;
    this.username = username;
    this.identity = { userId: username, name: username };
    this.room = null;
    this.local = null;
    this.screen = null;
    this.camera = null;
    this.connected = false;
    this.subscribed = new Set();
    this.subTries = new Map();
    this.audioNodes = new Map();
    this.callbacks = {};
    this.micPromise = null;
    this.micStream = null;
    this.micDeviceId = "";
    this.micLabel = "";
    this._preferId = "";
    this._preferLabel = "";
    this._micBusy = null;
    this.speakerId = "";
    this._lastMics = [];
    this._lastSpk = [];
    this._haveDevices = false;
    this._onDeviceChange = null;
    this._devTimer = null;
    this._micEnded = null;
    this._micTrack = null;
    this._recoverTimer = null;
    this._recoverTriedKey = "";
    this._mediaGo = null;
    this._playKeep = null;
    this._subRetry = new Map();
    this._streamRepairs = new Map();
    this._streamRepairTries = new Map();
    this._failedSignallingConnections = new WeakSet();
    this._leaving = false;
    this._reconnecting = false;
    this._wantMute = false;
    this._wantCam = false;
    this._wantBlur = false;
    this._wantShare = false;
    this._recordings = new Map();
    this.recordingContext = null;
    this.recordingEnabled = false;
    this._camMs = null;
    this._cameraFacing = "user";
    this._cameraSwitch = null;
    this._screenMs = null;
    this._forceTurn = false;
    this._pathFails = 0;
    this._pathArmAt = 0;
    this._sendBadAt = 0;
    this._mediaWatch = null;
    this._remoteWatch = new Map();
    this._peerMedia = new Map();
    this._mediaSentAt = 0;
    this._diagnostics = null;
    this._diagnosticSince = new Map();
    this._playErrors = new WeakMap();
  }

  setCallbacks(cb) {
    this.callbacks = cb || {};
  }

  beginRecover() {
    this._reconnecting = true;
  }

  endRecover() {
    this._reconnecting = false;
  }

  async connect(erizoSrc, config) {
    if (this._leaving) throw new Error("hangup");
    unlockAudio();
    this.watchDevices();
    const Erizo = await loadErizo(erizoSrc);
    if (this._leaving) throw new Error("hangup");
    if (Erizo.Logger) Erizo.Logger.setLogLevel(Erizo.Logger.ERROR);
    this.room = Erizo.Room({ token: this.token });
    this.bindRoom();
    try {
      await this.waitConnected();
    } catch (err) {
      this.dropRoom({ keepMic: true });
      throw err instanceof Error ? err : new Error("Room connection failed");
    }
    if (this._leaving) {
      this.dropRoom({ keepMic: true });
      throw new Error("hangup");
    }
    this._playKeep = setInterval(() => this.kickPlayback(), 4000);
    this.startMediaWatch();
    if (this.callbacks.onDiagnostics) {
      const onDiagnostics = this.callbacks.onDiagnostics;
      this._diagnostics = new MediaDiagnostics(
        () => this.diagnosticStreams(),
        onDiagnostics,
      );
      this._diagnostics.start();
    }
    if (config.role === "viewer") return;
    if (config.skipMic) this._wantMute = true;
    const keep = liveAudioTracks(this.micStream);
    await this.startLocalMedia({
      ...config,
      mediaStream: keep.length ? this.micStream : config.mediaStream || null,
    });
    if (this._leaving) {
      this.dropRoom({ keepMic: true });
      throw new Error("hangup");
    }
    if (config.restore) await this.restoreMedia();
  }

  async reconnect(token, config) {
    if (this._leaving) throw new Error("hangup");
    this._reconnecting = true;
    this.token = token;
    this.dropRoom({ keepMic: true });
    this.callbacks.onRemoteCleared?.();
    if (this._leaving) throw new Error("hangup");
    await this.connect(config.erizoSrc, { ...config, restore: true });
    if (this._leaving) {
      this.dropRoom({ keepMic: true });
      throw new Error("hangup");
    }
  }

  async restoreMedia() {
    this.muteAudio(!!this._wantMute);
    if (this.speakerId) {
      try {
        await this.setSpeaker(this.speakerId);
      } catch {
        /* ignore */
      }
    }
    if (this._wantCam) {
      try {
        await this.setVideo(true);
      } catch {
        this._wantCam = false;
      }
    }
    if (this._wantShare && liveVideoTracks(this._screenMs).length) {
      try {
        await this.startScreenShare();
      } catch {
        this._wantShare = false;
        this.callbacks.onScreenStream?.(null);
      }
    } else if (this._wantShare && !liveVideoTracks(this._screenMs).length) {
      this._wantShare = false;
      this.callbacks.onScreenStream?.(null);
    }
    this.announceMedia({ query: true });
  }

  currentMic() {
    const track = liveAudioTracks(this.micStream || this.local?.stream)[0];
    return {
      deviceId: trackDeviceId(track) || this.micDeviceId || "",
      label: track?.label || this.micLabel || "",
      live: !!track,
    };
  }

  canSelectSpeaker() {
    return (
      typeof HTMLMediaElement !== "undefined" &&
      typeof HTMLMediaElement.prototype.setSinkId === "function"
    );
  }

  async listAudioDevices() {
    if (!navigator.mediaDevices?.enumerateDevices) return { mics: [], speakers: [] };
    const devices = await navigator.mediaDevices.enumerateDevices();
    return {
      mics: listOfKind(devices, "audioinput", "Microphone"),
      speakers: this.canSelectSpeaker() ? listOfKind(devices, "audiooutput", "Speaker") : [],
    };
  }

  async listMics() {
    const { mics } = await this.listAudioDevices();
    return mics;
  }

  async listSpeakers() {
    const { speakers } = await this.listAudioDevices();
    return speakers;
  }

  currentSpeaker() {
    const id = this.speakerId || "";
    const found = (this._lastSpk || []).find((s) => s.deviceId === id);
    return {
      deviceId: id,
      label: found?.label || (id ? "Speaker" : "System default"),
    };
  }

  rememberMic(track, { preferred = false } = {}) {
    const id = trackDeviceId(track);
    const label = String(track?.label || "").trim();
    if (id) this.micDeviceId = id;
    if (label) this.micLabel = label;
    if (preferred) {
      if (id) this._preferId = id;
      if (label) this._preferLabel = label;
    }
  }

  bindMicTrack(track) {
    if (this._micTrack && this._micEnded) {
      this._micTrack.removeEventListener("ended", this._micEnded);
    }
    this._micTrack = track || null;
    this._micEnded = null;
    if (!track) return;
    this._micEnded = () => {
      this._recoverTriedKey = "";
      this.scheduleRecoverMic();
    };
    track.addEventListener("ended", this._micEnded);
  }

  watchDevices() {
    if (navigator.mediaDevices?.addEventListener && !this._onDeviceChange) {
      this._onDeviceChange = () => {
        if (this._devTimer) clearTimeout(this._devTimer);
        this._devTimer = setTimeout(() => {
          this._devTimer = null;
          this.refreshDevices()
            .then(() => {
              this.scheduleRecoverMic();
              return this.recoverSpeaker();
            })
            .catch(() => undefined);
        }, 300);
      };
      navigator.mediaDevices.addEventListener("devicechange", this._onDeviceChange);
    }
    this.refreshDevices({ silent: true }).catch(() => undefined);
  }

  unwatchDevices() {
    if (this._onDeviceChange && navigator.mediaDevices?.removeEventListener) {
      navigator.mediaDevices.removeEventListener("devicechange", this._onDeviceChange);
    }
    this._onDeviceChange = null;
    if (this._devTimer) {
      clearTimeout(this._devTimer);
      this._devTimer = null;
    }
    if (this._recoverTimer) {
      clearTimeout(this._recoverTimer);
      this._recoverTimer = null;
    }
    if (this._micTrack && this._micEnded) {
      this._micTrack.removeEventListener("ended", this._micEnded);
    }
    this._micTrack = null;
    this._micEnded = null;
  }

  async refreshDevices({ silent = false } = {}) {
    const { mics, speakers } = await this.listAudioDevices();
    const toast = !silent && this._haveDevices;
    const micDiff = toast ? deviceDiff(this._lastMics, mics) : { added: [], removed: [] };
    const spkDiff = toast ? deviceDiff(this._lastSpk, speakers) : { added: [], removed: [] };
    this._lastMics = mics;
    this._lastSpk = speakers;
    this._haveDevices = true;
    this.callbacks.onMicDevices?.(mics);
    this.callbacks.onDevices?.({
      mics,
      speakers,
      addedMics: micDiff.added,
      removedMics: micDiff.removed,
      addedSpeakers: spkDiff.added,
      removedSpeakers: spkDiff.removed,
    });
    return { mics, speakers };
  }

  applySink(el) {
    if (!el || typeof el.setSinkId !== "function") return;
    el.setSinkId(this.speakerId || "").catch(() => undefined);
  }

  applySinks() {
    this.audioNodes.forEach((node) => this.applySink(node.el));
  }

  async setSpeaker(deviceId) {
    this.speakerId = deviceId || "";
    this.applySinks();
  }

  async recoverSpeaker() {
    if (!this.canSelectSpeaker()) return;
    const speakers = this._lastSpk?.length ? this._lastSpk : await this.listSpeakers().catch(() => []);
    if (!this.speakerId || this.speakerId === "default") return;
    if (speakers.some((s) => s.deviceId === this.speakerId)) return;
    const prev = this.currentSpeaker();
    this.speakerId = "";
    this.applySinks();
    this.callbacks.onSpeakerLost?.(prev);
  }

  scheduleRecoverMic() {
    if (this._recoverTimer) clearTimeout(this._recoverTimer);
    this._recoverTimer = setTimeout(() => {
      this._recoverTimer = null;
      this.recoverMic().catch(() => undefined);
    }, 400);
  }

  matchPreferred(mics) {
    if (!mics?.length) return null;
    return (
      mics.find((m) => this._preferId && m.deviceId === this._preferId) ||
      mics.find((m) => this._preferLabel && m.label === this._preferLabel) ||
      null
    );
  }

  async recoverMic() {
    if (!this.connected) return;
    if (this._micBusy) {
      try {
        await this._micBusy;
      } catch {
        /* ignore */
      }
    }
    const listed = await this.refreshDevices({ silent: true }).catch(() => ({ mics: [] }));
    const mics = listed.mics || [];
    const key = mics.map((m) => m.deviceId).sort().join(",");
    const live = this.hasLocalAudio();
    const current = this.currentMic();
    const prefer = this.matchPreferred(mics);
    const onPreferred =
      !!prefer &&
      (prefer.deviceId === current.deviceId ||
        (prefer.label && current.label && prefer.label === current.label));
    if (live && onPreferred) {
      this._recoverTriedKey = key;
      return;
    }
    if (live && !prefer) {
      this._recoverTriedKey = key;
      return;
    }
    if (live && this._recoverTriedKey === key) return;
    const next = prefer || (live ? null : mics[0]);
    if (!next) {
      if (!live) {
        this.micStream = null;
        this.micPromise = null;
        this.callbacks.onMicLost?.({ deviceId: this.micDeviceId, label: this.micLabel });
      }
      return;
    }
    if (live && prefer && !onPreferred) {
      this._recoverTriedKey = key;
      await this.switchMic(prefer.deviceId, { preferred: false, reason: "recovered" });
      return;
    }
    if (!live) {
      this._recoverTriedKey = key;
      await this.switchMic(next.deviceId, {
        preferred: false,
        reason: prefer ? "recovered" : "fallback",
      });
    }
  }

  captureMic(deviceId) {
    const want = deviceId || this._preferId || "";
    const live = liveAudioTracks(this.micStream);
    if (live.length) {
      const cur = trackDeviceId(live[0]);
      if (!want || !cur || cur === want) return Promise.resolve(this.micStream);
    }
    if (this.micPromise && !deviceId) return this.micPromise;
    if (!navigator.mediaDevices?.getUserMedia) {
      return Promise.reject(new Error("No microphone API"));
    }
    this.micPromise = this.openMic(want)
      .then((ms) => {
        this.micStream = ms;
        const track = liveAudioTracks(ms)[0];
        this.rememberMic(track, { preferred: !this._preferId });
        this.bindMicTrack(track);
        this.refreshDevices({ silent: true }).catch(() => undefined);
        return ms;
      })
      .catch((err) => {
        this.micPromise = null;
        throw err;
      });
    this._mediaGo?.();
    this._mediaGo = null;
    return this.micPromise;
  }

  async openMic(deviceId) {
    const attempts = [];
    if (deviceId) attempts.push({ audio: { deviceId: { exact: deviceId } } });
    if (deviceId) attempts.push({ audio: { deviceId: { ideal: deviceId } } });
    attempts.push({ audio: true });
    let lastErr;
    for (const constraints of attempts) {
      try {
        return await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        lastErr = err;
      }
    }
    throw lastErr || new Error("No microphone");
  }

  async startLocalMedia(config) {
    const Erizo = window.Erizo;
    if (!Erizo) throw new Error("erizo.js is not loaded");
    const ident = config.identity || {};
    this.identity = {
      userId: ident.userId || ident.userid || this.username,
      name: ident.name || this.username,
      avatarUrl: ident.avatarUrl || ident.avatar || "",
      punkId: ident.punkId ?? null,
      address: ident.address || "",
    };
    const displayName = this.identity.name;
    let ms = config.mediaStream || null;
    if (!ms && !config.skipMic) {
      try {
        ms = await Promise.race([
          this.captureMic(config.deviceId || this._preferId),
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error("Microphone timeout — tap the screen")), 12000);
          }),
        ]);
      } catch (err) {
        if (config.requireMic !== false) throw err;
      }
    }
    const tracks = liveAudioTracks(ms);
    const hasAudio = tracks.length > 0;
    if (config.requireMic !== false && !hasAudio) {
      throw new Error("Microphone required for this room");
    }
    this.local = Erizo.Stream({
      audio: hasAudio,
      data: true,
      attributes: {
        name: displayName,
        actualName: displayName,
        nickname: displayName,
        userid: this.identity.userId,
        id: this.identity.userId,
        avatar: this.identity.avatarUrl || "",
        avatarUrl: this.identity.avatarUrl || "",
        punkId: this.identity.punkId,
        address: this.identity.address || "",
        type: hasAudio ? "audio" : "presence",
        role: "presenter",
      },
    });
    if (hasAudio) {
      this.local.stream = ms;
      this.micStream = ms;
      this.holdLocalMic(this.local);
      tracks.forEach((t) => {
        t.enabled = true;
      });
      this.rememberMic(tracks[0], { preferred: !this._preferId });
      this.bindMicTrack(tracks[0]);
    }
    await this.publish(this.local, { maxVideoBW: 300, record: hasAudio ? "audio" : "" });
    if (!hasAudio) this._wantMute = true;
    this.muteAudio(!!this._wantMute);
    this.callbacks.onLocalStream?.(this.local);
    this.refreshDevices({ silent: true }).catch(() => undefined);
    this.announceMedia({ query: true });
  }

  holdLocalMic(stream) {
    const ms = mediaStreamOf(stream);
    if (!ms?.getAudioTracks?.().length) return;
    let el = document.getElementById("bm-local-mic");
    if (!el) {
      el = document.createElement("audio");
      el.id = "bm-local-mic";
      el.dataset.localMic = "1";
      el.muted = true;
      el.volume = 0;
      el.autoplay = true;
      el.setAttribute("playsinline", "true");
      el.style.cssText = "position:fixed;left:-9999px;width:1px;height:1px";
      document.body.appendChild(el);
    }
    if (el.srcObject !== ms) el.srcObject = ms;
    el.play().catch(() => undefined);
  }

  muteAudio(muted) {
    this._wantMute = !!muted;
    this.local?.muteAudio?.(muted);
    const ms = this.local?.stream || this.micStream;
    liveAudioTracks(ms).forEach((t) => {
      t.enabled = !muted;
    });
    this.announceMedia();
  }

  hasLocalAudio() {
    return liveAudioTracks(this.local?.stream || this.micStream).length > 0;
  }

  async enableMic() {
    if (this.hasLocalAudio()) {
      this.muteAudio(false);
      return;
    }
    await this.switchMic(this._preferId, { preferred: false, reason: "enable" });
  }

  async switchMic(deviceId, opts = {}) {
    if (this._micBusy) {
      try {
        await this._micBusy;
      } catch {
        /* ignore */
      }
    }
    this._micBusy = this._switchMic(deviceId, opts).finally(() => {
      this._micBusy = null;
    });
    return this._micBusy;
  }

  async _switchMic(deviceId, { preferred = true, reason = "switch" } = {}) {
    const want = deviceId || this._preferId || "";
    const muted = !!this.local?.audioMuted;
    const old = this.micStream;
    this.micStream = null;
    this.micPromise = null;
    let ms;
    try {
      ms = await this.captureMic(want);
    } catch (err) {
      this.micStream = old;
      throw err;
    }
    const track = liveAudioTracks(ms)[0];
    if (!track) throw new Error("No microphone");
    this.rememberMic(track, { preferred });
    const got = trackDeviceId(track);
    const gotPrefer =
      !want || got === want || (!!this._preferLabel && track.label === this._preferLabel);
    const notifyReason = reason === "recovered" && !gotPrefer ? "fallback" : reason;

    if (!this.local || !this.connected) {
      this.callbacks.onMicChanged?.({ ...this.currentMic(), reason: notifyReason });
      return;
    }

    const attached = await this.replaceLocalAudio(track);
    if (!attached) {
      if (this.local) {
        try {
          this.room?.unpublish?.(this.local);
        } catch {
          /* ignore */
        }
        this.local = null;
      }
      await this.startLocalMedia({
        identity: this.identity,
        mediaStream: ms,
        skipMic: false,
        requireMic: true,
      });
    }
    if (old && old !== this.micStream && old !== this.local?.stream) {
      old.getTracks?.().forEach((t) => {
        if (t !== track) {
          t.onended = null;
          t.stop();
        }
      });
    }
    if (muted) this.muteAudio(true);
    else this.muteAudio(false);
    this.callbacks.onMicChanged?.({ ...this.currentMic(), reason: notifyReason });
  }

  async replaceLocalAudio(track) {
    const dest = this.local?.stream;
    if (!dest) return false;
    try {
      dest.getAudioTracks?.().forEach((t) => {
        if (t === track) return;
        dest.removeTrack(t);
        t.onended = null;
        t.stop();
      });
      if (!dest.getAudioTracks?.().includes(track)) dest.addTrack(track);
    } catch {
      return false;
    }
    this.micStream = dest;
    this.holdLocalMic(this.local);
    this.bindMicTrack(track);
    const senders = rtcPeerConnections(this.local).flatMap((pc) =>
      pc.getSenders().filter((s) => !s.track || s.track.kind === "audio")
    );
    if (!senders.length) return false;
    try {
      await Promise.all(senders.map((s) => s.replaceTrack(track)));
      return true;
    } catch {
      return false;
    }
  }

  attrs(extra) {
    const displayName = this.identity.name || this.username;
    return {
      ...publishedAttributes(
        {
          ...this.identity,
          name: displayName,
          userId: this.identity.userId || this.username,
        },
        {},
      ),
      role: "presenter",
      ...extra,
    };
  }

  /** Write a profile that arrived after publish onto the local streams. */
  applyIdentity(identity) {
    if (!identity) return;
    const userId = identity.userId || identity.userid || this.identity.userId || this.username;
    this.identity = {
      userId,
      name: identity.name || this.identity.name || this.username,
      avatarUrl: identity.avatarUrl || identity.avatar || this.identity.avatarUrl || "",
      punkId: identity.punkId ?? this.identity.punkId ?? null,
      address: identity.address || this.identity.address || "",
    };
    for (const stream of [this.local, this.camera, this.screen]) {
      if (!stream || typeof stream.setAttributes !== "function") continue;
      try {
        stream.setAttributes(publishedAttributes(this.identity, stream.getAttributes?.() || {}));
      } catch {
        /* a stream can leave while the profile arrives */
      }
    }
  }

  async setVideo(enabled) {
    if (!enabled) {
      this._wantCam = false;
      if (!this.camera) {
        stopTracks(this._camMs);
        this._camMs = null;
        this.announceMedia();
        return;
      }
      try {
        this.stopRecordingFor(this.camera);
        this.room?.unpublish?.(this.camera);
        this.camera.close?.();
      } catch {
        /* ignore */
      }
      this.camera = null;
      this._blurTrack?.removeEventListener?.("configurationchange", this._onBlurChange);
      this._blurTrack = null;
      this.callbacks.onCameraBlur?.(this.cameraBlurState());
      stopTracks(this._camMs);
      this._camMs = null;
      this.callbacks.onCameraStream?.(null);
      this.announceMedia();
      return;
    }
    this._wantCam = true;
    if (this.camera) return;
    const Erizo = window.Erizo;
    const cam = Erizo.Stream({
      audio: false,
      video: { facingMode: { ideal: this._cameraFacing } },
      data: true,
      attributes: this.attrs({ type: "video" }),
    });
    const kept = liveVideoTracks(this._camMs).length ? this._camMs : null;
    if (kept) cam.stream = kept;
    else await this.initStream(cam);
    this._camMs = mediaStreamOf(cam) || this._camMs;
    await this.publish(cam, { audio: false, video: true, maxVideoBW: 1500, handlerProfile: 0, record: "video" });
    this.camera = cam;
    await this.applyPreferredBlur();
    this.callbacks.onCameraStream?.(cam);
    this.announceMedia();
  }

  cameraVideoTrack() {
    return liveVideoTracks(mediaStreamOf(this.camera))[0] || null;
  }

  cameraBlurState() {
    const track = this.cameraVideoTrack();
    if (!track) return { controllable: false, known: false, on: null };
    this.watchBlur(track);
    return blurStatus(track);
  }

  watchBlur(track) {
    if (!track || track === this._blurTrack) return;
    this._blurTrack?.removeEventListener?.("configurationchange", this._onBlurChange);
    this._blurTrack = track;
    this._onBlurChange = () => {
      const state = blurStatus(track);
      if (state.controllable) this._wantBlur = !!state.on;
      this.callbacks.onCameraBlur?.(state);
    };
    track.addEventListener?.("configurationchange", this._onBlurChange);
  }

  async applyPreferredBlur() {
    const track = this.cameraVideoTrack();
    const state = blurControl(track);
    if (!state) return null;
    this.watchBlur(track);
    if (!this._wantBlur) return state;
    try {
      return { on: await setBackgroundBlur(track, true) };
    } catch {
      this._wantBlur = false;
      return blurControl(track);
    }
  }

  async setCameraBlur(on) {
    const track = this.cameraVideoTrack();
    if (!blurControl(track)) return null;
    const enabled = await setBackgroundBlur(track, on);
    this._wantBlur = enabled;
    this.watchBlur(track);
    return { on: enabled };
  }

  async switchCamera() {
    if (this._cameraSwitch) return this._cameraSwitch;
    const cam = this.camera;
    const room = this.room;
    if (!cam || !this.connected) throw new Error("Turn the camera on first");
    const media = mediaStreamOf(cam);
    const tracks = media?.getVideoTracks?.() || [];
    const senders = [...new Set(rtcPeerConnections(cam).flatMap((pc) => pc.getSenders()))]
      .filter((sender) => tracks.includes(sender.track));
    this._cameraSwitch = switchCameraTrack({
      media,
      senders,
      previousFacing: this._cameraFacing,
      isCurrent: () => this.camera === cam && this.room === room && this._wantCam && !this._leaving,
    });
    try {
      this._cameraFacing = await this._cameraSwitch;
      await this.applyPreferredBlur();
      this.callbacks.onCameraBlur?.(this.cameraBlurState());
    } finally {
      this._cameraSwitch = null;
    }
  }

  async startScreenShare() {
    if (!this.connected) throw new Error("Not connected");
    this._wantShare = true;
    if (this.screen) return;
    const Erizo = window.Erizo;
    const screenStream = Erizo.Stream({
      screen: true,
      video: true,
      data: true,
      attributes: this.attrs({
        type: "screen",
        screen: true,
        name: `${this.identity.name || this.username} · screen`,
      }),
    });
    const kept = liveVideoTracks(this._screenMs).length ? this._screenMs : null;
    if (kept) {
      screenStream.stream = kept;
    } else {
      await this.initStream(screenStream);
    }
    this._screenMs = mediaStreamOf(screenStream) || this._screenMs;
    await this.publish(screenStream, { audio: false, video: true, handlerProfile: 0, record: "screen" });
    this.screen = screenStream;
    this.callbacks.onScreenStream?.(screenStream);
    this.announceMedia();
  }

  stopScreenShare() {
    this._wantShare = false;
    if (!this.screen) {
      stopTracks(this._screenMs);
      this._screenMs = null;
      this.announceMedia();
      return;
    }
    try {
      this.stopRecordingFor(this.screen);
      this.room?.unpublish?.(this.screen);
      this.screen.close?.();
    } finally {
      this.screen = null;
      stopTracks(this._screenMs);
      this._screenMs = null;
      this.callbacks.onScreenStream?.(null);
      this.announceMedia();
    }
  }

  sendPayload(msg) {
    const stream = this.local || this.screen || this.camera;
    if (!stream?.sendData) return false;
    try {
      stream.sendData(msg);
      return true;
    } catch {
      return false;
    }
  }

  mediaState() {
    const mic = this.hasLocalAudio();
    return {
      mute: !!this._wantMute || !mic,
      mic,
      cam: !!this.camera,
      share: !!this.screen,
    };
  }

  announceMedia({ query = false } = {}) {
    if (!this.connected || this._leaving) return;
    this._mediaSentAt = Date.now();
    const state = this.mediaState();
    this.sendPayload({
      type: "media",
      ...state,
      from: this.identity.name || this.username,
      userid: this.identity.userId || this.username,
    });
    if (query) this.sendPayload({ type: "media-query", userid: this.identity.userId || this.username });
  }

  applyPeerMedia(msg, stream) {
    const userid = peerUserId(stream, msg);
    const self = String(this.identity.userId || this.username);
    if (!userid || userid === self) return;
    const rec = {
      mute: !!msg.mute,
      mic: msg.mic !== false,
      cam: !!msg.cam,
      share: !!msg.share,
    };
    this._peerMedia.set(userid, rec);
    this.callbacks.onPeerMedia?.({ userid, ...rec });
  }

  peerMediaOf(stream) {
    const userid = peerUserId(stream);
    return userid ? this._peerMedia.get(userid) : undefined;
  }

  sendChat(text) {
    return this.sendPayload({
      type: "chat",
      text,
      from: this.identity.name || this.username,
      userid: this.identity.userId || this.username,
      avatar: this.identity.avatarUrl || "",
      punkId: this.identity.punkId,
    });
  }

  dropRoom({ keepMic = false } = {}) {
    this.stopAllRecordings();
    this._diagnostics?.stop();
    this._diagnostics = null;
    this._diagnosticSince.clear();
    if (this._playKeep) {
      clearInterval(this._playKeep);
      this._playKeep = null;
    }
    this.stopMediaWatch();
    this._peerMedia.clear();
    this._mediaSentAt = 0;
    this._subRetry.forEach((t) => clearTimeout(t));
    this._subRetry.clear();
    this._streamRepairs.forEach((repair) => clearTimeout(repair.timer));
    this._streamRepairs.clear();
    this._streamRepairTries.clear();
    this._failedSignallingConnections = new WeakSet();
    this.subscribed.clear();
    this.subTries.clear();
    this.audioNodes.forEach((node) => {
      try {
        node.stream?.hide?.();
        node.el?.pause?.();
      } catch {
        /* ignore */
      }
    });
    this.audioNodes.clear();
    document.querySelectorAll("div[id^='audio-']").forEach((el) => el.remove());
    const camMs = mediaStreamOf(this.camera) || this._camMs;
    const screenMs = mediaStreamOf(this.screen) || this._screenMs;
    if (keepMic) {
      this._camMs = keepLive(camMs) || this._camMs;
      this._screenMs = keepLive(screenMs) || this._screenMs;
    } else {
      stopTracks(this._camMs);
      stopTracks(this._screenMs);
      this._camMs = null;
      this._screenMs = null;
      this._wantCam = false;
      this._wantShare = false;
    }
    const room = this.room;
    this.room = null;
    this.connected = false;
    this.screen = null;
    this.camera = null;
    this.local = null;
    try {
      room?.disconnect?.();
    } catch {
      /* ignore */
    }
    if (!keepMic) {
      this.unwatchDevices();
      document.getElementById("bm-local-mic")?.remove();
      this.micPromise?.then((ms) => ms.getTracks?.().forEach((t) => t.stop())).catch(() => undefined);
      this.micStream?.getTracks?.().forEach((t) => t.stop());
      this.micPromise = null;
      this.micStream = null;
      this._mediaGo = null;
    }
  }

  /** A restarted join on this same instance. disconnect() latches leave. */
  beginJoin() {
    this._leaving = false;
  }

  disconnect() {
    this._leaving = true;
    this.dropRoom({ keepMic: false });
  }

  bindRoom() {
    const room = this.room;
    const isCurrent = () => this.room === room;
    room.addEventListener("stream-added", (evt) => {
      if (!isCurrent()) return;
      const stream = evt?.stream;
      if (!stream?.getID) return;
      if (isLocalId(stream, this.local, this.screen, this.camera)) return;
      this.subscribe(stream);
      this.announceMedia();
      this.callbacks.onRemoteAdded?.(stream);
    });
    room.addEventListener("stream-removed", (evt) => {
      if (!isCurrent()) return;
      const stream = evt?.stream;
      const id = stream?.getID?.();
      if (!id) return;
      if (isLocalId(stream, this.local, this.screen, this.camera)) return;
      this.subscribed.delete(id);
      this._remoteWatch.delete(id);
      this.subTries.delete(id);
      const repair = this._streamRepairs.get(id);
      if (repair) clearTimeout(repair.timer);
      this._streamRepairs.delete(id);
      this._streamRepairTries.delete(id);
      if (this._subRetry.has(id)) {
        clearTimeout(this._subRetry.get(id));
        this._subRetry.delete(id);
      }
      const node = this.audioNodes.get(`audio-${id}`);
      try {
        node?.stream?.hide?.();
        node?.el?.pause?.();
      } catch {
        /* ignore */
      }
      this.audioNodes.delete(`audio-${id}`);
      document.getElementById(`audio-${id}`)?.remove();
      this.callbacks.onRemoteRemoved?.(stream);
    });
    room.addEventListener("stream-subscribed", (evt) => {
      if (!isCurrent()) return;
      const stream = evt?.stream;
      if (!stream?.getID) return;
      try {
        stream.muteAudio?.(false);
      } catch {
        /* ignore */
      }
      this.playAudio(stream);
      this.watchRemote(stream);
      const repair = this._streamRepairs.get(stream.getID());
      if (repair) {
        clearTimeout(repair.timer);
        this._streamRepairs.delete(stream.getID());
      }
      this.announceMedia();
      stream.addEventListener?.("stream-data", (e) => this.onData(e?.msg, stream));
      stream.addEventListener?.("stream-attributes-update", () => {
        this.callbacks.onRemoteSubscribed?.(stream);
      });
      this.callbacks.onRemoteSubscribed?.(stream);
    });
    room.addEventListener("stream-unsubscribed", (evt) => {
      if (!isCurrent()) return;
      const stream = evt?.stream;
      const id = stream?.getID?.();
      const repair = id ? this._streamRepairs.get(id) : null;
      if (!id || !repair) return;
      if (this._leaving || this._reconnecting || !this.connected) return;
      this.subscribe(stream, { forceTurn: true, repairReason: repair.reason });
    });
    room.addEventListener("stream-attributes-update", (evt) => {
      if (!isCurrent()) return;
      const stream = evt?.stream;
      if (stream?.getID) this.callbacks.onRemoteSubscribed?.(stream);
    });
    room.addEventListener("stream-data", (evt) => {
      if (!isCurrent()) return;
      this.onData(evt?.msg, evt?.stream);
    });
    room.addEventListener("stream-failed", (evt) => {
      if (!isCurrent()) return;
      const stream = evt?.stream;
      const id = stream?.getID?.();
      if (!id || isLocalId(stream, this.local, this.screen, this.camera)) return;
      this.repairRemoteStream(stream, "stream-failed", { alreadyUnsubscribing: true });
    });
    room.addEventListener("connection-failed", (evt) => {
      if (!isCurrent() || this._leaving || this._reconnecting) return;
      const connection = evt?.connection;
      if (!connection || typeof connection !== "object") return;
      // ErizoConnection forwards native server failure messages as this event.
      // Match the current wrapper by identity: a delayed event from a replaced
      // connection must not tear down the replacement, even with the same ID.
      const owns = (stream) => stream?.pc === connection;
      const reason = evt.message === "Ice Connection failure detected in server"
        ? "server-connection-failed" : "signalling-connection-failed";
      if ([this.local, this.camera, this.screen].some(owns)) {
        // Keep a terminal failure across the recovery cooldown. Browser ICE
        // may still say connected even though the native server PC has failed.
        this._failedSignallingConnections.add(connection);
        if (this.connected) this.failMediaPath(`${reason}-send`);
        return;
      }
      room.remoteStreams?.forEach?.((stream) => {
        if (!owns(stream)) return;
        // Unlike stream-failed, this event does not unsubscribe in erizo.js.
        // Start cleanup here, then await stream-unsubscribed before retrying.
        this.repairRemoteStream(stream, reason);
      });
    });
    room.addEventListener("room-connected", (evt) => {
      if (!isCurrent()) return;
      const list = [];
      const fromEvent = evt?.streams;
      if (Array.isArray(fromEvent)) list.push(...fromEvent);
      const map = room.remoteStreams;
      if (map && typeof map.forEach === "function") map.forEach((s) => list.push(s));
      else if (map) list.push(...Object.values(map));
      const seen = new Set();
      list.forEach((stream) => {
        const id = stream?.getID?.();
        if (!id || seen.has(id) || isLocalId(stream, this.local, this.screen, this.camera)) return;
        seen.add(id);
        this.subscribe(stream);
        this.callbacks.onRemoteAdded?.(stream);
      });
    });
    room.addEventListener("room-disconnected", (evt) => {
      if (!isCurrent() || this._leaving || this._reconnecting) return;
      this.connected = false;
      this.callbacks.onDisconnected?.(eventMessage(evt, "Lost connection to the room"));
    });
    room.addEventListener("room-error", (evt) => {
      if (!isCurrent() || this._leaving || this._reconnecting || !this.connected) return;
      this.connected = false;
      this.callbacks.onDisconnected?.(eventMessage(evt, "Room connection failed"));
    });
    room.addEventListener("room-reconnecting", () => {
      if (!isCurrent() || this._leaving || this._reconnecting) return;
      this.callbacks.onReconnecting?.();
    });
    room.addEventListener("room-reconnected", () => {
      if (!isCurrent() || this._leaving || this._reconnecting) return;
      this.connected = true;
      this.callbacks.onSocketReconnected?.();
    });
  }

  onData(msg, stream) {
    if (!msg) return;
    if (typeof msg !== "string" && msg.type === "media-query") {
      this.announceMedia();
      return;
    }
    if (typeof msg !== "string" && msg.type === "media") {
      this.applyPeerMedia(msg, stream);
      return;
    }
    const text = typeof msg === "string" ? msg : msg.text;
    if (!text) return;
    const attrs = stream?.getAttributes?.() || {};
    const from = (typeof msg === "string" ? "" : msg.from) || attrs.name || attrs.actualName || "Participant";
    this.callbacks.onChat?.({
      who: from,
      text,
      ts: Date.now(),
      userid: typeof msg === "string" ? attrs.userid || "" : msg.userid || attrs.userid || "",
      avatarUrl:
        typeof msg === "string"
          ? attrs.avatar || attrs.avatarUrl || ""
          : msg.avatar || msg.avatarUrl || attrs.avatar || attrs.avatarUrl || "",
      punkId: typeof msg === "string" ? attrs.punkId ?? null : msg.punkId ?? attrs.punkId ?? null,
    });
  }

  clearRemotePlayback(stream) {
    const id = stream?.getID?.();
    if (!id) return;
    const key = `audio-${id}`;
    const node = this.audioNodes.get(key);
    try {
      node?.stream?.hide?.();
      node?.el?.pause?.();
    } catch {
      /* ignore */
    }
    this.audioNodes.delete(key);
    if (typeof document !== "undefined") document.getElementById(key)?.remove();
  }

  failStreamRepair(id, reason) {
    const repair = this._streamRepairs.get(id);
    if (repair) clearTimeout(repair.timer);
    this._streamRepairs.delete(id);
    this.subscribed.delete(id);
    this._remoteWatch.delete(id);
    this.failMediaPath(`${reason}-resubscribe-failed`);
  }

  repairRemoteStream(stream, reason, { alreadyUnsubscribing = false } = {}) {
    const id = stream?.getID?.();
    if (!id || !this.room || this._leaving || this._reconnecting || !this.connected) return false;
    if (this._streamRepairs.has(id)) return true;
    const tries = (this._streamRepairTries.get(id) || 0) + 1;
    if (tries > STREAM_REPAIR_MAX) {
      this.failMediaPath(`${reason}-resubscribe-exhausted`);
      return false;
    }
    this._streamRepairTries.set(id, tries);
    this.subscribed.delete(id);
    this._remoteWatch.delete(id);
    this.subTries.delete(id);
    if (this._subRetry.has(id)) {
      clearTimeout(this._subRetry.get(id));
      this._subRetry.delete(id);
    }
    this.clearRemotePlayback(stream);
    const timer = setTimeout(
      () => this.failStreamRepair(id, reason),
      STREAM_REPAIR_TIMEOUT_MS,
    );
    this._streamRepairs.set(id, { stream, reason, timer });
    const attrs = stream.getAttributes?.() || {};
    this.callbacks.onStreamRepair?.({
      streamId: String(id),
      reason,
      name: attrs.actualName || attrs.name || attrs.nickname || "participant",
      attempt: tries,
    });
    if (alreadyUnsubscribing) return true;
    try {
      this.room.unsubscribe(stream, (ok, err) => {
        if (!ok || err) this.failStreamRepair(id, reason);
      });
    } catch {
      this.failStreamRepair(id, reason);
    }
    return true;
  }

  repairRemoteById(streamId) {
    const id = String(streamId || "");
    if (!id || !this.room || this._leaving || this._reconnecting || !this.connected) return false;
    let found = null;
    this.room.remoteStreams?.forEach?.((stream) => {
      if (String(stream?.getID?.()) === id) found = stream;
    });
    if (!found || isLocalId(found, this.local, this.screen, this.camera)) return false;
    return this.repairRemoteStream(found, "delivery-gap");
  }

  scheduleSubscribe(stream, options = {}) {
    const id = stream?.getID?.();
    if (!id || !this.room) return;
    const n = (this.subTries.get(id) || 0) + 1;
    if (n > 8) {
      if (options.repairReason) this.failMediaPath(`${options.repairReason}-subscribe-exhausted`);
      return;
    }
    this.subTries.set(id, n);
    if (this._subRetry.has(id)) clearTimeout(this._subRetry.get(id));
    const t = setTimeout(() => {
      this._subRetry.delete(id);
      this.subscribe(stream, options);
    }, Math.min(12000, 400 * n));
    this._subRetry.set(id, t);
  }

  watchRemote(stream) {
    const id = stream?.getID?.();
    if (!id || isVideoOnly(stream)) return;
    if (typeof stream.hasAudio === "function" && !stream.hasAudio()) return;
    this._remoteWatch.set(id, { stream, since: Date.now(), badAt: 0 });
  }

  startMediaWatch() {
    this.stopMediaWatch();
    this._sendBadAt = 0;
    this._pathArmAt = Math.max(
      this._pathArmAt || 0,
      Date.now() + (this._forceTurn ? PATH_QUIET_TURN_MS : PATH_QUIET_MS)
    );
    this._mediaWatch = setInterval(() => {
      this.checkMediaPath().catch(() => undefined);
    }, PATH_POLL_MS);
  }

  stopMediaWatch() {
    if (this._mediaWatch) {
      clearInterval(this._mediaWatch);
      this._mediaWatch = null;
    }
    this._remoteWatch.clear();
  }

  failMediaPath(reason) {
    if (this._leaving || this._reconnecting || !this.connected) return;
    if (Date.now() < this._pathArmAt) return;
    if (this._pathFails >= 3) return;
    this._pathFails += 1;
    this._forceTurn = true;
    this._pathArmAt = Date.now() + PATH_COOLDOWN_MS;
    this.callbacks.onOneWay?.(reason);
  }

  heldBad(rec, now) {
    if (!rec.badAt) rec.badAt = now;
    return now - rec.badAt >= PATH_CONFIRM_MS;
  }

  async checkMediaPath() {
    if (this._leaving || this._reconnecting || !this.connected) return;
    if (Date.now() - this._mediaSentAt >= 8000) this.announceMedia();
    if (Date.now() < this._pathArmAt) return;
    const now = Date.now();

    if ([this.local, this.camera, this.screen].some(
      (stream) => stream?.pc && this._failedSignallingConnections.has(stream.pc)
    )) {
      this.failMediaPath("signalling-connection-failed-send");
      return;
    }

    if (this.local) {
      let sendAgentnet = false;
      const pcs = rtcPeerConnections(this.local);
      let anyOk = false;
      let allFailed = pcs.length > 0;
      for (const pc of pcs) {
        const s = await inspectPc(pc);
        if (s.agentnet) sendAgentnet = true;
        if (s.iceFailed) continue;
        allFailed = false;
        anyOk = true;
      }
      const reason = sendAgentnet
        ? "agentnet-ice-send"
        : allFailed && !anyOk
          ? "ice-failed-send"
          : "";
      if (reason) {
        if (!this._sendBadAt) this._sendBadAt = now;
        else if (now - this._sendBadAt >= PATH_CONFIRM_MS) {
          this.failMediaPath(reason);
          return;
        }
      } else {
        this._sendBadAt = 0;
      }
    }

    for (const rec of this._remoteWatch.values()) {
      const stream = rec.stream;
      if (!stream?.getID) continue;
      const pcs = rtcPeerConnections(stream);
      if (!pcs.length) continue;
      if (now - rec.since < PATH_QUIET_MS) continue;
      let agentnet = false;
      let anyOk = false;
      let allFailed = true;
      for (const pc of pcs) {
        const s = await inspectPc(pc);
        if (s.agentnet) agentnet = true;
        if (s.iceFailed) continue;
        allFailed = false;
        anyOk = true;
      }
      const reason = agentnet
        ? "agentnet-ice-recv"
        : allFailed && !anyOk
          ? "ice-failed-recv"
          : "";
      if (reason) {
        if (this.heldBad(rec, now)) {
          this.repairRemoteStream(stream, reason);
          return;
        }
      } else {
        rec.badAt = 0;
        this._streamRepairTries.delete(String(stream.getID()));
      }
    }
  }

  subscribe(stream, options = {}) {
    const id = stream.getID();
    if (this.subscribed.has(id)) return;
    this.subscribed.add(id);
    this.room.subscribe(
      stream,
      {
        audio: true,
        video: true,
        data: true,
        slideShowMode: false,
        forceTurn: options.forceTurn ?? !!this._forceTurn,
      },
      (_ok, err) => {
        if (!err) {
          this.subTries.delete(id);
          // The repair timer stays until stream-subscribed attaches media.
          return;
        }
        this.subscribed.delete(id);
        this.scheduleSubscribe(stream, options);
      }
    );
  }

  kickPlayback() {
    unlockAudio();
    this.audioNodes.forEach((node) => this.primeAudioEl(node.el));
  }

  diagnosticStreams() {
    const streams = [];
    const seen = new Set();
    const now = Date.now();
    const take = (stream, direction) => {
      if (!stream?.getID || isVideoOnly(stream) || stream.hasAudio?.() === false) return;
      const streamId = String(stream.getID());
      if (!streamId || seen.has(streamId)) return;
      seen.add(streamId);
      if (!this._diagnosticSince.has(streamId)) this._diagnosticSince.set(streamId, now);
      const node = this.audioNodes.get(`audio-${streamId}`);
      streams.push({ streamId, direction, pcs: rtcPeerConnections(stream),
        mediaStream: mediaStreamOf(stream), element: node?.el,
        playError: node?.el ? this._playErrors.get(node.el) : "",
        ageMs: now - this._diagnosticSince.get(streamId),
        subscribed: direction === "recv" ? !!node : null,
        muted: direction === "send" ? this._wantMute : this.peerMediaOf(stream)?.mute ?? null });
    };
    take(this.local, "send");
    const remotes = this.room?.remoteStreams;
    if (typeof remotes?.forEach === "function") remotes.forEach((s) => take(s, "recv"));
    else Object.values(remotes || {}).forEach((s) => take(s, "recv"));
    for (const id of this._diagnosticSince.keys()) if (!seen.has(id)) this._diagnosticSince.delete(id);
    return streams;
  }

  primeAudioEl(el) {
    if (!el) return false;
    el.autoplay = true;
    el.muted = false;
    el.defaultMuted = false;
    el.volume = 1;
    el.playsInline = true;
    el.setAttribute("playsinline", "true");
    this.applySink(el);
    const p = el.play?.();
    if (p && typeof p.then === "function") p.then(
      () => this._playErrors.delete(el),
      (err) => this._playErrors.set(el, ["NotAllowedError", "NotSupportedError", "AbortError"].includes(err?.name)
        ? err.name : "PlaybackError"),
    );
    return !el.paused && !el.muted;
  }

  playAudio(stream) {
    if (isVideoOnly(stream)) return;
    if (typeof stream.hasAudio === "function" && !stream.hasAudio()) return;
    const sid = stream.getID();
    const id = `audio-${sid}`;
    const attach = () => {
      let box = document.getElementById(id);
      if (!box) {
        box = document.createElement("div");
        box.id = id;
        box.style.cssText =
          "position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;";
        document.body.appendChild(box);
      }
      try {
        if (typeof stream.show === "function" && !stream.showing) {
          stream.show(id, { bar: false, speaker: false });
        }
      } catch {
        /* HTMLAudioElement fallback below */
      }
      const el =
        box.querySelector("audio, video") ||
        document.getElementById(`stream${sid}`);
      const ms = mediaStreamOf(stream);
      const tracks = ms?.getAudioTracks?.() || [];
      tracks.forEach((t) => {
        t.enabled = true;
        t.onunmute = () => this.primeAudioEl(this.audioNodes.get(id)?.el);
      });
      if (el) {
        this.primeAudioEl(el);
        this.audioNodes.set(id, { el, stream });
        return !el.paused;
      }
      if (!ms || tracks.length === 0) return false;
      let audio = document.getElementById(`${id}-el`);
      if (!audio) {
        audio = document.createElement("audio");
        audio.id = `${id}-el`;
        audio.autoplay = true;
        audio.setAttribute("playsinline", "true");
        box.appendChild(audio);
      }
      if (audio.srcObject !== ms) audio.srcObject = ms;
      this.primeAudioEl(audio);
      this.audioNodes.set(id, { el: audio, stream });
      return !audio.paused;
    };
    if (attach()) return;
    const t = setInterval(() => {
      if (attach()) clearInterval(t);
    }, 250);
    setTimeout(() => clearInterval(t), 120000);
    stream.addEventListener?.("added-stream", attach);
  }

  waitConnected() {
    const room = this.room;
    return new Promise((resolve, reject) => {
      let settled = false;
      const t = setTimeout(() => fail(new Error("Room connection timeout")), 15000);
      const ok = () => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        this.connected = true;
        resolve();
      };
      const fail = (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        reject(err instanceof Error ? err : new Error(eventMessage(err, "Room connection failed")));
      };
      room.addEventListener("room-connected", ok);
      room.addEventListener("room-error", fail);
      room.addEventListener("room-disconnected", fail);
      if (this._leaving) {
        fail(new Error("hangup"));
        return;
      }
      try {
        room.connect();
      } catch (err) {
        fail(err);
      }
    });
  }

  initStream(stream) {
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        clearInterval(poll);
        if (err) reject(err);
        else resolve();
      };
      const t = setTimeout(() => finish(new Error("Media access timeout")), 15000);
      stream.addEventListener("access-accepted", () => finish());
      stream.addEventListener("access-denied", () =>
        finish(new Error("Camera or microphone permission denied"))
      );
      stream.init();
      const poll = setInterval(() => {
        const n = stream.stream?.getAudioTracks?.()?.length || 0;
        const v = stream.stream?.getVideoTracks?.()?.length || 0;
        if (n + v > 0) finish();
      }, 200);
    });
  }

  publish(stream, opts = { maxVideoBW: 300 }) {
    return new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error("Publish timeout")), 30000);
      const { record, ...publishOpts } = opts;
      this.room.publish(stream, { ...publishOpts, forceTurn: !!this._forceTurn }, (_id, error) => {
        clearTimeout(t);
        if (error) reject(error);
        else {
          this.beginRecording(stream, record);
          resolve();
        }
      });
    });
  }

  beginRecording(stream, kind) {
    // Off unless the server says recording is enabled (RECORDING_ENABLED).
    if (this.recordingEnabled !== true) return;
    const room = this.room;
    const ctx = this.recordingContext;
    if (!kind || !room?.startRecording || !ctx?.roomId) return;
    const streamId = String(stream?.getID?.() || "");
    room.startRecording(stream, (id, err) => {
      if (id == null) {
        console.info(`[beagle-meet] recording skipped kind=${kind}${err ? ` ${err}` : ""}`);
        return;
      }
      const recordingId = String(id);
      if (streamId) this._recordings.set(streamId, recordingId);
      if (ctx.apiBase == null) return;
      registerRecording(ctx.apiBase, ctx.roomId, {
        recordingId,
        streamId,
        kind,
        roomName: ctx.roomName || "",
        meetingSessionId: ctx.meetingSessionId || "",
        participantSessionId: ctx.participantSessionId || "",
        connectionId: ctx.connectionId || "",
      });
    });
  }

  stopRecordingFor(stream) {
    const streamId = String(stream?.getID?.() || "");
    const recordingId = streamId ? this._recordings.get(streamId) : "";
    if (!recordingId) return;
    this._recordings.delete(streamId);
    const room = this.room;
    const apiBase = this.recordingContext?.apiBase;
    const finish = () => {
      if (apiBase != null) finishRecording(apiBase, recordingId);
    };
    try {
      if (room?.stopRecording) room.stopRecording(recordingId, finish);
      else finish();
    } catch {
      finish();
    }
  }

  stopAllRecordings() {
    const room = this.room;
    const apiBase = this.recordingContext?.apiBase;
    for (const recordingId of this._recordings.values()) {
      const finish = () => {
        if (apiBase != null) finishRecording(apiBase, recordingId);
      };
      try {
        if (room?.stopRecording) room.stopRecording(recordingId, finish);
        else finish();
      } catch {
        finish();
      }
    }
    this._recordings.clear();
  }
}
